import { useEffect, useRef } from 'react';

const problems = [
  {
    img: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/25d83207-e4cb-4051-858f-125550677720_1.png?v=69d98614e753affb31e76a03ad661a0f',
    title: '실리콘 균열 및 이물질 축적',
    desc: '실리콘이 경화 후 수축·균열되어 틈이 벌어지고, 이물질이 끼어 방수 기능이 상실됩니다',
  },
  {
    img: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/efe970c5-e5d8-4a82-90a1-9eb3c7df3713_2.png?v=034cbef3c48cedb80ba1b8d05f21ddb1',
    title: '볼트 주변 누수 발생',
    desc: '실리콘 접착력이 상실되면서 볼트와 캡 사이로 빗물이 직접 침투합니다',
  },
  {
    img: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/e4ab432e-1225-4b85-bd84-b80da2f94263_3.png?v=80bb176c80076cd89a4f7c05539081fb',
    title: '자외선 열화로 캡 자체 파손',
    desc: '기존 캡 소재가 자외선과 온도 변화에 취약하여 2~3년 내 약화·파손됩니다',
  },
  {
    img: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/00074e94-7dee-4df7-bac3-552b4ed7b0b1_4.png?v=32d51385a602ff1fd4cc34199cfe7a23',
    title: '파손 후 볼트 완전 노출',
    desc: '캡이 부서진 후 방치되면 볼트가 녹슬고, 패널 관통부가 그대로 노출됩니다',
  },
  {
    img: 'https://storage.readdy-site.link/project_files/a91d34d6-979b-40c4-a28b-dffad304fbfe/a8bdf51e-b2c3-40c7-848c-d8f53c442def_-1.png?v=a196d069f61ce3de54698ced084b26b0',
    title: '실리콘 통째 박리',
    desc: '실리콘이 캡·패널 표면에서 통째로 벗겨져 방수층이 완전히 사라집니다',
  },
];

const ProblemSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    if (footerRef.current) observer.observe(footerRef.current);
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="problem" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="text-center mb-14 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">Field Problem</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            기존 Roof Cap, 현장에서 이런 문제가 발생합니다
          </h2>
          <p className="mt-3 text-gray-500 text-base max-w-xl mx-auto">
            실리콘 도포 방식의 구조적 한계
          </p>
          <div className="mt-4 w-12 h-1 bg-red-500 mx-auto rounded-full" />
        </div>

        {/* 카드 그리드: 상단 3 + 하단 2 중앙 정렬 */}
        <div className="space-y-6">
          {/* 상단 3개 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {problems.slice(0, 3).map((item, i) => (
              <div
                key={i}
                ref={(el) => { cardRefs.current[i] = el; }}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden opacity-0 translate-y-8 transition-all duration-600 hover:border-red-200 group"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* 이미지 - 1:1 정사각형 */}
                <div className="w-full aspect-square overflow-hidden relative">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* 경고 뱃지 */}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    <i className="ri-error-warning-fill text-xs" />
                    하자 사례
                  </div>
                </div>
                {/* 텍스트 */}
                <div className="p-5">
                  <div className="flex items-start gap-2.5 mb-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-fill text-red-500 text-xs" />
                    </div>
                    <h3 className="font-bold text-[#1B2A4A] text-sm leading-snug">{item.title}</h3>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed pl-8">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* 하단 2개 중앙 정렬 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:max-w-2xl md:mx-auto lg:max-w-3xl">
            {problems.slice(3).map((item, i) => (
              <div
                key={i + 3}
                ref={(el) => { cardRefs.current[i + 3] = el; }}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden opacity-0 translate-y-8 transition-all duration-600 hover:border-red-200 group"
                style={{ transitionDelay: `${(i + 3) * 100}ms` }}
              >
                {/* 이미지 - 1:1 정사각형 */}
                <div className="w-full aspect-square overflow-hidden relative">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    <i className="ri-error-warning-fill text-xs" />
                    하자 사례
                  </div>
                </div>
                {/* 텍스트 */}
                <div className="p-5">
                  <div className="flex items-start gap-2.5 mb-2">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-fill text-red-500 text-xs" />
                    </div>
                    <h3 className="font-bold text-[#1B2A4A] text-sm leading-snug">{item.title}</h3>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed pl-8">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 하단 강조 배너 */}
        <div
          ref={footerRef}
          className="mt-12 opacity-0 translate-y-6 transition-all duration-700 delay-500"
        >
          <div className="bg-[#1B2A4A] rounded-2xl overflow-hidden">
            {/* 상단: 원인 강조 */}
            <div className="bg-red-500/10 border-b border-red-500/20 px-6 py-5 flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500/20 flex-shrink-0">
                <i className="ri-alert-fill text-red-400 text-xl" />
              </div>
              <p className="text-red-300 font-semibold text-sm sm:text-base leading-relaxed">
                이 모든 문제의 원인 →
                <span className="text-white font-bold ml-2">
                  실리콘 &apos;별도 도포&apos; 방식의 구조적 한계
                </span>
              </p>
            </div>
            {/* 하단: 해결책 */}
            <div className="px-6 py-5 flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-500/20 flex-shrink-0">
                <i className="ri-shield-check-fill text-green-400 text-xl" />
              </div>
              <p className="text-gray-300 text-sm sm:text-base leading-relaxed">
                <span className="text-[#FF6B35] font-bold">Smart Cap</span>
                <span className="text-white">은 실리콘 패킹이 일체형으로 내장되어</span>
                <span className="text-green-400 font-bold ml-1">이런 문제가 원천적으로 발생하지 않습니다</span>
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ProblemSection;
